import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
} from '@mui/material';
import toast from "react-hot-toast";

import { FruitSizeMeasurement } from '../../../types/fruitsize';
import { MonitoringMode } from './MonitoringMode';
import { EditSelectAttribute } from '../../../components/EditSelectAttribute';
import { useMutationSaveFruitSizeMeasurements, useQueryRetrieveBlocks, useQueryRetrieveFruitSizeMeasurements, useQueryRetrieveFruitSizeMeasurementStats, useQueryRetrieveTrees } from '../../../services/stores/queries';
import { TreeCard } from './TreeCard';
import { useBlocks, useCultures, useTrees, useVarieties } from '../../../helpers/hooks';
import { Tree } from '../../../types/tree';
import { Histogram } from '../../../components/Histogram';
import { FruitSizeInputModal } from './FruitSizeInputModal';
import { MeasurementCacheService } from '../../../services/measurementCacheService';
import calipersIcon from '../../../assets/img/app/calipers.svg';
import { useQueryClient } from '@tanstack/react-query';
import { Block } from '../../../types/block';
import { Variety } from '../../../types/variety';
import { Culture } from '../../../types';
import { ApplicationsCalendar, DateClassNames } from '../../../components/ApplicationsCalendar';
import { CandlestickChart } from '../../../components/CandlestickChart';
import { getFruitSizeStats } from '../../../helpers/stats';
import { SampleCountStats } from './SampleCountStats';
import { ConfirmationModal } from '../../../components/ConfirmationModal';

export const FruitSize: React.FC = () => {
  const [measurements, setFruitSizeMeasurements] = useState<FruitSizeMeasurement[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [mode, setMode] = useState<"TREE" | "BLOCK" | "FRUIT">("TREE");
  const [selectedTree, setSelectedTree] = useState<Tree | undefined>(undefined);
  const [selectedBlock, setSelectedBlock] = useState<Block | undefined>(undefined);
  const [selectedVariety, setSelectedVariety] = useState<Variety | undefined>(undefined);
  const [selectedCulture, setSelectedCulture] = useState<Culture | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [syncStatus, setSyncStatus] = useState<{ pending: number; lastSync: number }>({ pending: 0, lastSync: 0 });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);

  const fruitID = undefined;

  const dateClassNames: DateClassNames = {};
  
  const { data: trees, 
          error: errorTrees, 
          isLoading: isLoadingTrees } = useQueryRetrieveTrees();

  const { data: blocks, 
          error: errorBlocks, 
          isLoading: isLoadingBlocks } = useQueryRetrieveBlocks();

  const { data: dbFruitSizeMeasurements, 
          error: errorDBFruitSizeMeasurements, 
          isLoading: isLoadingDBFruitSizeMeasurements } = useQueryRetrieveFruitSizeMeasurements(selectedBlock?.id, selectedVariety?.id, selectedTree?.id, fruitID, selectedDate);  

  const { data: dbFruitSizeMeasurementStats, 
          error: errorDBFruitSizeMeasurementStats, 
          isLoading: isLoadingDBFruitSizeMeasurementStats } = useQueryRetrieveFruitSizeMeasurementStats(selectedBlock?.id, selectedVariety?.id, selectedTree?.id, fruitID);  

  const { data: dbFruitSizeMeasurementsByVariety, 
          error: errorDBFruitSizeMeasurementsByVariety, 
          isLoading: isLoadingDBFruitSizeMeasurementsByVariety } = useQueryRetrieveFruitSizeMeasurements(selectedBlock?.id, selectedVariety?.id, undefined, undefined, selectedDate);  

  const {toTree} = useTrees();
  const {toBlock} = useBlocks();
  const {toCulture} = useCultures();
  const {toVariety} = useVarieties(selectedCulture?.id);

  useEffect(() => {
    if (mode === "TREE") {
      setSelectedBlock(toBlock(selectedTree?.blockID));
      setSelectedCulture(toCulture(selectedTree?.cultureID));
      setSelectedVariety(toVariety(selectedTree?.varietyID));
    }
  }, [mode, selectedTree]);

  const mutateSaveMeasurements = useMutationSaveFruitSizeMeasurements();
  const queryClient = useQueryClient();

  // Load measurements from cache on component mount
  useEffect(() => {
    const cachedMeasurements = MeasurementCacheService.loadMeasurements();
    const dbMeasurements = dbFruitSizeMeasurements || [];

    setFruitSizeMeasurements(cachedMeasurements.concat(dbMeasurements));
    
    // Update sync status
    const stats = MeasurementCacheService.getCacheStats();
    const status = MeasurementCacheService.loadSyncStatus();
    setSyncStatus({
      pending: stats.pending,
      lastSync: status.lastSyncTime
    });
  }, [dbFruitSizeMeasurements]);


  const getTreeMeasurements = () => {
    if (!selectedTree) return [];
    return measurements.filter(m => m.treeID === selectedTree.id && m.status !== 'deleted');
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    handleSyncToBackend(true);
  };

  const clearMeasurements = () => {
    setFruitSizeMeasurements([]);
    MeasurementCacheService.saveMeasurements([]);
    const stats = MeasurementCacheService.getCacheStats();
    MeasurementCacheService.saveSyncStatus({
      lastSyncTime: Date.now(),
      pendingCount: stats.pending
    });

    setSyncStatus({
      pending: stats.pending,
      lastSync: Date.now()
    });
  };

  const handleMeasurementsUpdate = (newMeasurements: FruitSizeMeasurement[]) => {
    setFruitSizeMeasurements(newMeasurements);
    // Save to cache
    MeasurementCacheService.saveMeasurements(newMeasurements);
    
    // Update sync status
    const stats = MeasurementCacheService.getCacheStats();
    setSyncStatus(prev => ({ ...prev, pending: stats.pending }));
  };

  const handleSyncToBackend = async (silentError: boolean = false) => {
    
    try {
      const pendingMeasurements = MeasurementCacheService.getPendingMeasurements();
      if (pendingMeasurements.length === 0) {
        console.log('No pending measurements to sync');
        return;
      }

      mutateSaveMeasurements.mutate(measurements,
        {
          onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['fruitSizeMeasurements']});
            queryClient.invalidateQueries({queryKey: ['fruitSizeMeasurementStats']});
            toast.success('Les mesures de calibre des fruits ont été enregistrés avec succès');
            
            // Mark as synced
            const measurementIds = pendingMeasurements.map(m => m.id);
            MeasurementCacheService.markAsSynced(measurementIds);
            
            const cachedMeasurements = MeasurementCacheService.loadMeasurements();
            setFruitSizeMeasurements(cachedMeasurements);
          // Update sync status
          const stats = MeasurementCacheService.getCacheStats();
          MeasurementCacheService.saveSyncStatus({
            lastSyncTime: Date.now(),
            pendingCount: stats.pending
          });
      
          setSyncStatus({
            pending: stats.pending,
            lastSync: Date.now(),
          });

          },
          onError: (error: Error) => {
            if (!silentError) { // if silentError is true, don't show the error message
              alert(error.message)
              toast.error(error.message);
            }
        }
      })
            
      console.log('Sync completed successfully');
    } catch (error) {
      console.error('Error syncing to backend:', error);
      MeasurementCacheService.saveSyncStatus({
        lastSyncTime: syncStatus.lastSync,
        pendingCount: MeasurementCacheService.getCacheStats().pending,
        lastError: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const handleTreeChange = (value: string) => {
    setSelectedTree(toTree(value));
    const resetMeasurements = measurements.map(m => ({...m, selected: false})); 
    setFruitSizeMeasurements(resetMeasurements);
    handleMeasurementsUpdate(resetMeasurements);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  const handleClearMeasurementsConfirmation = () => {
    clearMeasurements();
    setConfirmationDialogOpen(false);
  };

  const selectedTreeMeasurements = getTreeMeasurements();
  const stats = getFruitSizeStats(selectedTreeMeasurements);
  const statsByVariety = getFruitSizeStats(dbFruitSizeMeasurementsByVariety || []);

  const isToday = selectedDate.getDate() === new Date().getDate() && 
    selectedDate.getMonth() === new Date().getMonth() && 
    selectedDate.getFullYear() === new Date().getFullYear();

  if (mode !== "TREE") {
    return (
            <div
      style={{ 
        maxWidth: '500px', 
        backgroundColor: 'var(--bs-body-bg)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column'
      }}>    

      <MonitoringMode 
        mode={mode} 
        setMode={setMode} 
      />
        <h4 style={{ textAlign: 'center', marginTop: '20px', color: 'red' }}>Ce mode n&apos;est pas disponible pour le moment</h4>
      </div>
    )
  }

  return (
    <div
      style={{ 
        maxWidth: '500px', 
        backgroundColor: 'var(--bs-body-bg)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column'
      }}>    

      <MonitoringMode 
        mode={mode} 
        setMode={setMode} 
      />

      {mode === "TREE" && (
        <React.Fragment>
          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            <EditSelectAttribute 
              name = "treeID"
              label= "Arbre"
              value = {selectedTree?.id || ''}
              options = {trees || []}
              disabled = {false}
              labelField = "description"
              valueField = "id"
              onChangeCallback = {(value: string) => {handleTreeChange(value);}}
            />
          </div>

          <TreeCard
            tree={selectedTree}
            showName={false}
          />

        </React.Fragment>
      )}

      <ApplicationsCalendar 
              dateClassNames={dateClassNames} 
              selectedDate={selectedDate} 
              onDateChange={handleDateChange}
              showFullCalendarInitially={false}
              showNavigationButtons={false}
              autoHideOnDateSelect={true}
            />

  {isToday && (
      <Button 
        variant="contained" 
        onClick={handleOpenModal}
        sx={{ 
          mb: 2, 
          mt: 2, 
          backgroundColor: 'green', 
          '&:hover': { backgroundColor: 'darkgreen' },
          textTransform: 'none'
        }}
        startIcon={
          <img 
            src={calipersIcon}
            alt="Calipers" 
            style={{ width: '60px', height: '60px', filter: 'brightness(0) invert(1)', transform: 'rotate(30deg)' }}
          />
        }
      >
        Saisir les mesures
      </Button>
      )}

      {syncStatus.pending > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {syncStatus.pending} mesure(s) en attente de synchronisation
          <Button 
            size="small" 
            onClick={() => handleSyncToBackend(false)}
            sx={{ ml: 1 }}
          >
            Synchroniser
          </Button>
        </Alert>
      )}



      <FruitSizeInputModal
        open={modalOpen}
        onClose={handleCloseModal}
        onMeasurementsUpdate={handleMeasurementsUpdate}
        existingMeasurements={measurements}
        selectedTree={selectedTree}
      />

      {selectedTreeMeasurements.length > 0 && (
        <React.Fragment>

        <SampleCountStats 
          nSamples={selectedTreeMeasurements.length}
          n95={stats.n95}
          confidence={stats.confidence || 0}
        />

      <TableContainer component={Paper} sx={{ mb: 5, mt: 1 }}>
        <Table size="small">
          <TableHead style={{ backgroundColor: 'lightgray' }}>
            <TableRow>
              <TableCell style={{ width: '20%' }} >Min</TableCell>
              <TableCell style={{ width: '20%' }}>Moyenne</TableCell>
              <TableCell style={{ width: '20%' }}>Max</TableCell>
              <TableCell style={{ width: '20%' }}>N 95%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{stats.min.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{stats.average.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{stats.max.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{stats.n95} </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper}>
        <Table size="small" sx={{ width: '100%' }}>
        <TableHead style={{ backgroundColor: 'lightgray' }}>
           <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Calibre (mm)</TableCell>
              <TableCell>Heure</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...selectedTreeMeasurements]
              .sort((a, b) => (new Date(b.timestamp)).getTime() - (new Date(a.timestamp)).getTime())
              .map((measurement, index) => (
              <TableRow key={measurement.id}>
                <TableCell>{selectedTreeMeasurements.length - index}</TableCell>
                <TableCell>{measurement.diameter_mm.toFixed(2)}</TableCell>
                <TableCell>
                  {(new Date(measurement.timestamp)).toLocaleString('fr-FR', {
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>



      <Histogram 
          data={selectedTreeMeasurements.map(m => m.diameter_mm)} 
          title="Calibres par arbre"
        />

      {dbFruitSizeMeasurementsByVariety && dbFruitSizeMeasurementsByVariety.length > 0 && (
        <React.Fragment>
                <TableContainer component={Paper} sx={{ mb: 5, mt: 1 }}>
        <Table size="small">
          <TableHead style={{ backgroundColor: 'lightgray' }}>
            <TableRow>
              <TableCell style={{ width: '20%' }} >Min</TableCell>
              <TableCell style={{ width: '20%' }}>Moyenne</TableCell>
              <TableCell style={{ width: '20%' }}>Max</TableCell>
              <TableCell style={{ width: '20%' }}>Conf</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{statsByVariety.min.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{statsByVariety.average.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{statsByVariety.max.toFixed(2)} mm</TableCell>
              <TableCell style={{ width: '20%', textAlign: 'center' }}>{statsByVariety.confidence?.toFixed(1)} %</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

       <Histogram 
          data={dbFruitSizeMeasurementsByVariety?.map(m => m.diameter_mm)} 
          title="Calibres par variété"
        />
        </React.Fragment>
        )}

      </React.Fragment>
)}


{dbFruitSizeMeasurementStats && dbFruitSizeMeasurementStats.length > 1 && (
    <CandlestickChart 
      data={dbFruitSizeMeasurementStats || []} 
      title={"Evolution du calibre"}
    />
)}

{syncStatus.pending > 0  && (
    <Button 
        variant="contained" 
        onClick={() => setConfirmationDialogOpen(true)}
        sx={{ 
          mb: 2, 
          mt: 2, 
          backgroundColor: 'red', 
          '&:hover': { backgroundColor: 'red' },
          textTransform: 'none'
        }}
      >
        Supprimer toutes les mesures non synchronisées
      </Button>
      )}
    


    <ConfirmationModal
      show={confirmationDialogOpen}
      onNo={() => setConfirmationDialogOpen(false)}
      onYes={handleClearMeasurementsConfirmation}
      title="Cette action est à utiliser avec précaution"
      message="Êtes-vous sûr de vouloir supprimer toutes les mesures non synchronisées ?  Cette action est irréversible et vous allez perdre toutes les mesures non synchronisées.  "
      yesLabel="Supprimer"
      noLabel="Annuler"
    />

    </div>
  );
};


